import { useEffect, useMemo, useRef, useState } from "react"
import { router, usePage } from "@inertiajs/react"
import {
  AlertTriangle,
  ArrowRight,
  Check,
  Circle,
  Clock,
  Copy,
  MessageSquare,
  Mic,
  MicOff,
  MonitorUp,
  PhoneOff,
  ShieldCheck,
  Video as VideoIcon,
  VideoOff,
  X,
} from "lucide-react"

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Input } from "@/components/ui/input"
import { Avatar } from "@/components/tuw/Avatar"
import MessageThread from "@/components/tuw/chat/MessageThread"
import ChatComposer from "@/components/tuw/chat/ChatComposer"
import useOptimisticSend from "@/hooks/useOptimisticSend"
import useVideoRoom from "@/lib/videoRoom"
import useCallPreview from "@/lib/callPreview"
import { cn } from "@/lib/utils"

// ---------------------------------------------------------------------------
// Video call — the full React/Inertia rebuild of the erb + Stimulus surface.
// One page, seven runtime phases (Surface 9 / video-call), in its own dark
// full-screen layout (NO AppShell — the page exports no `.layout`). The Twilio
// transport lives in useVideoRoom; this file is the declarative render + the
// reused side-chat plumbing. Dark-locked (brand-red accent), tokens only.
// ---------------------------------------------------------------------------

const POLL_MS = 4000

function initialsOf(name) {
  return (name || "?")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase()
}

// Attach a Twilio MediaTrack (video/audio) to a real DOM node in an effect keyed
// on the track, so toggling chat/screen never re-attaches or orphans the element.
// `fit` defaults to filling the box, which is right for a camera tile — a face
// should reach the edges. A shared screen is the opposite: cropping it loses the
// content it exists to show, so that one call site asks for `contain` (AT-17).
function VideoSurface({ track, mirror = false, fit = "cover", className }) {
  const ref = useRef(null)
  useEffect(() => {
    const node = ref.current
    if (!node || !track) return undefined
    const el = track.attach()
    el.style.width = "100%"
    el.style.height = "100%"
    el.style.objectFit = fit
    if (mirror) el.style.transform = "scale(-1, 1)"
    node.appendChild(el)
    return () => {
      track.detach().forEach((e) => e.remove())
      if (node.contains(el)) node.removeChild(el)
    }
  }, [track, mirror, fit])
  return <div ref={ref} className={cn("absolute inset-0 overflow-hidden", className)} />
}

function AudioSurface({ track, sinkId }) {
  const ref = useRef(null)
  useEffect(() => {
    if (!track) return undefined
    const el = track.attach()
    if (sinkId && typeof el.setSinkId === "function") el.setSinkId(sinkId).catch(() => {})
    ref.current = el
    document.body.appendChild(el)
    return () => {
      track.detach().forEach((e) => e.remove())
    }
  }, [track, sinkId])
  return null
}

// A single call tile — real video when a track is present + enabled, avatar
// otherwise (honest fidelity: avatar by default, real feed when it arrives).
function Tile({ name, initials, videoTrack, cameraOff, muted, mirror = false, self = false, compact = false }) {
  const showVideo = videoTrack && !cameraOff
  return (
    <div className="relative flex min-h-0 items-center justify-center overflow-hidden rounded-[18px] border border-border bg-secondary">
      {showVideo ? (
        <VideoSurface track={videoTrack} mirror={mirror} />
      ) : (
        <Avatar initials={initials} name={name} size={compact ? 64 : 104} me={self} />
      )}
      <div className="absolute bottom-3.5 left-3.5 z-10 flex items-center gap-2 rounded-[9px] bg-black/60 px-2.5 py-1.5 text-[12.5px] font-semibold text-white backdrop-blur">
        <span className="grid size-[15px] place-items-center">
          {muted ? <MicOff className="size-[14px] text-primary" strokeWidth={2} /> : <Mic className="size-[14px]" strokeWidth={2} />}
        </span>
        {self ? "You" : name}
      </div>
      {cameraOff && !self && (
        <div className="absolute top-3.5 right-3.5 z-10 flex items-center gap-1.5 rounded-lg bg-black/55 px-2.5 py-1 text-[11px] font-semibold text-white backdrop-blur">
          <VideoOff className="size-3" strokeWidth={2} /> Camera off
        </div>
      )}
    </div>
  )
}

// ---- top status bar --------------------------------------------------------
function TopBar({ room, timeLeftLabel, warn, contextLabel, onCopy }) {
  return (
    // Wraps because the row's rigid width — wordmark, room pill, timer pill,
    // padding and gaps — is wider than a portrait phone, and the call surface is
    // `fixed inset-0`, so anything overflowing is pushed off screen with no way to
    // scroll it back. Above roughly 640px it still fits on one line (AT-12).
    <div className="z-10 flex shrink-0 flex-wrap items-center gap-3.5 px-[22px] py-4">
      <div className="flex items-center gap-2.5">
        <div className="grid size-7 place-items-center rounded-lg bg-primary font-display text-[15px] font-bold text-white">D</div>
        <div className="font-display text-sm font-semibold tracking-[-0.2px]">Dibbly</div>
      </div>
      {contextLabel && <div className="truncate text-[12.5px] text-ink-3">{contextLabel}</div>}
      <div className="flex-1" />
      {room && (
        <div className="inline-flex items-center gap-2.5 rounded-[10px] border border-border bg-card py-1.5 pr-2 pl-3">
          <span className="text-[10px] font-bold tracking-[0.07em] text-ink-3 uppercase">Room</span>
          <span className="font-display text-[12.5px] font-semibold">{room}</span>
          <button
            type="button"
            onClick={onCopy}
            title="Copy room code"
            className="grid size-[26px] place-items-center rounded-[7px] bg-secondary text-muted-foreground hover:bg-primary hover:text-white"
          >
            <Copy className="size-[13px]" strokeWidth={2} />
          </button>
        </div>
      )}
      {timeLeftLabel && (
        <div
          className={cn(
            "inline-flex items-center gap-2 rounded-[10px] border border-border bg-card px-3 py-2 text-[12.5px] font-semibold text-muted-foreground",
            warn && "border-needs-you/40 text-needs-you"
          )}
        >
          <Clock className="size-3.5" strokeWidth={2} /> Time left <b className="font-display tabular-nums text-foreground">{timeLeftLabel}</b>
        </div>
      )}
    </div>
  )
}

// ---- control bar -----------------------------------------------------------
function ControlButton({ icon: Icon, label, active, off, badge = 0, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      // The count is the whole point of the badge, so it belongs in the name a
      // screen reader announces; the visual pill is decorative once it is there.
      aria-label={badge > 0 ? `${label}, ${badge} unread` : undefined}
      className="flex flex-col items-center gap-1.5"
    >
      <span
        className={cn(
          "relative grid size-[54px] place-items-center rounded-2xl border transition",
          off || active ? "border-primary bg-primary text-white" : "border-border bg-card text-foreground hover:bg-secondary"
        )}
      >
        <Icon className="size-[22px]" strokeWidth={1.9} />
        {badge > 0 && (
          <span
            aria-hidden="true"
            className="absolute -top-1.5 -right-1.5 grid h-[19px] min-w-[19px] place-items-center rounded-full border-2 border-background bg-primary px-1 font-display text-[10px] font-bold text-white tabular-nums"
          >
            {badge > 9 ? "9+" : badge}
          </span>
        )}
      </span>
      <span className="text-[11px] font-semibold text-muted-foreground">{label}</span>
    </button>
  )
}

function ControlBar({ micOn, camOn, canScreen, screenOn, chatOpen, chatUnread, onMic, onCam, onScreen, onChat, onLeave }) {
  return (
    <div className="z-10 flex shrink-0 items-center justify-center gap-2.5 px-[22px] pt-4 pb-[22px]">
      <ControlButton icon={micOn ? Mic : MicOff} label="Mute" off={!micOn} onClick={onMic} />
      <ControlButton icon={camOn ? VideoIcon : VideoOff} label="Camera" off={!camOn} onClick={onCam} />
      {/* On a phone there is no screen-capture API, so this control could only
          ever fail silently — it exists only where the browser can share (AU-21). */}
      {canScreen && <ControlButton icon={MonitorUp} label="Share" active={screenOn} onClick={onScreen} />}
      <ControlButton icon={MessageSquare} label="Chat" active={chatOpen} badge={chatUnread} onClick={onChat} />
      <button
        type="button"
        onClick={onLeave}
        className="ml-1 inline-flex h-[54px] items-center gap-2.5 rounded-2xl bg-primary px-6 font-display text-sm font-semibold text-white hover:bg-red-dark"
      >
        <PhoneOff className="size-5" strokeWidth={2} /> Leave
      </button>
    </div>
  )
}

// ---- device select ---------------------------------------------------------
// The empty-string placeholder is a *selectable off-state* the app acts on
// (`micId=""` → mic off / "No Audio"), not a prompt — so it stays a real,
// selectable item under a "__none__" sentinel with bidirectional `""↔sentinel`
// mapping. Radix `SelectItem` rejects `value=""`, so the empty string never
// reaches an item; choosing the off-state still sets `""` downstream.
const DEVICE_NONE = "__none__"

function DeviceSelect({ label, icon: Icon, value, onChange, options, placeholder, container }) {
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-semibold text-ink-3">{label}</label>
      <div className="flex items-center gap-2.5 rounded-[11px] border border-border bg-secondary px-3.5 py-2.5">
        <Icon className="size-4 shrink-0 text-muted-foreground" strokeWidth={2} />
        <Select
          value={value || DEVICE_NONE}
          onValueChange={(v) => onChange(v === DEVICE_NONE ? "" : v)}
        >
          <SelectTrigger
            size="sm"
            className="w-full border-none bg-transparent px-0 text-[13px] text-foreground shadow-none focus-visible:ring-0"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent container={container}>
            <SelectItem value={DEVICE_NONE}>{placeholder}</SelectItem>
            {options.map((d) => (
              <SelectItem key={d.deviceId} value={d.deviceId}>
                {d.label || "Device"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    </div>
  )
}

// ---- side chat (reuses the Messages message plumbing: send + poll) ----------
function SideChat({ conversationSid, onClose }) {
  const sideChat = usePage().props.sideChat ?? { sid: null, messages: [] }
  const composerRef = useRef(null)

  // The server thread arrives from the `sideChat` prop, refreshed by the Inertia
  // partial reload below (send + poll) — the same reuse the order Chat tab uses.
  const serverItems = useMemo(
    () =>
      (sideChat.messages ?? []).map((m) => ({
        kind: "message",
        id: m.id,
        mine: m.mine,
        who: m.who,
        role: m.role,
        at: m.at,
        body: m.body,
      })),
    [sideChat.messages]
  )

  const { pendingItems, send, retry, editAndRemove, dismiss, sending } = useOptimisticSend({
    sid: conversationSid,
    only: ["sideChat"],
    serverItems,
  })

  // The poll that keeps this thread current lives on the page, not here — this
  // component unmounts with the panel, and a poll that stops when the panel shuts
  // can never know a message arrived while it was shut (AT-11).
  return (
    <div className="flex w-[330px] shrink-0 flex-col overflow-hidden rounded-[18px] border border-border bg-card">
      <div className="flex shrink-0 items-center justify-between border-b border-border px-4 py-3.5">
        <h3 className="font-display text-sm font-semibold">In-call chat</h3>
        <button
          type="button"
          onClick={onClose}
          title="Close chat"
          className="grid size-7 place-items-center rounded-lg bg-secondary text-muted-foreground hover:text-foreground"
        >
          <X className="size-[15px]" strokeWidth={2.2} />
        </button>
      </div>
      <MessageThread
        items={[...serverItems, ...pendingItems]}
        onRetry={retry}
        onEdit={(id) => composerRef.current?.refill(editAndRemove(id))}
        onDismiss={dismiss}
        empty={
          <div className="m-auto max-w-[220px] text-center">
            <MessageSquare className="mx-auto size-6 text-ink-3" strokeWidth={1.6} />
            <p className="mt-2 text-[12px] leading-[1.5] text-muted-foreground">No messages yet. Say hello.</p>
          </div>
        }
      />
      {conversationSid ? (
        <ChatComposer ref={composerRef} placeholder="Message everyone…" onSend={send} sending={sending} hint={null} />
      ) : (
        <div className="shrink-0 border-t border-border bg-secondary px-4 py-3 text-center text-[12px] text-ink-3">
          Chat opens once the other person joins.
        </div>
      )}
    </div>
  )
}

// ===========================================================================
// Phases
// ===========================================================================
function PreJoin({
  vc,
  role,
  partner,
  me,
  limits,
  policyUrl,
  recordingNotice,
  inProgress,
  canStartInstant,
  portalNode,
  origin,
  initialRoomCode,
  resumedNotice,
}) {
  const [code, setCode] = useState(initialRoomCode ?? "")
  const [rejoinCode, setRejoinCode] = useState(inProgress?.[0] ?? "")
  const partnerFirst = partner?.name?.split(" ")[0]

  const canStart = !vc.noAudio && vc.supported

  if (!vc.supported) {
    return (
      <div className="flex flex-1 items-center justify-center px-6 text-center">
        <div className="max-w-[440px] rounded-[22px] border border-border bg-card p-10">
          <AlertTriangle className="mx-auto mb-4 size-7 text-needs-you" strokeWidth={1.9} />
          <h1 className="font-display text-lg font-bold">Your browser doesn't support video calls</h1>
          <p className="mt-2.5 text-[13.5px] text-muted-foreground">
            Please use the latest version of Chrome, Firefox or Safari and reload this page.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex flex-1 items-center justify-center overflow-y-auto px-[22px] py-2">
      <div className="grid w-full max-w-[880px] overflow-hidden rounded-[22px] border border-border bg-card md:grid-cols-[1.15fr_1fr]">
        {/* preview */}
        <div className="relative flex min-h-[300px] items-center justify-center bg-secondary">
          {vc.camId && vc.previewTrack ? (
            <VideoSurface track={vc.previewTrack} mirror />
          ) : (
            <div className="flex flex-col items-center gap-3.5">
              <Avatar initials={initialsOf(me.name)} name={me.name} size={96} me />
              <div className="text-[12.5px] text-ink-3">You · camera preview</div>
            </div>
          )}
        </div>

        {/* form */}
        <div className="flex flex-col p-8">
          <div className="text-[11px] font-bold tracking-[0.08em] text-primary uppercase">Start a video call</div>
          <h1 className="mt-1.5 font-display text-[23px] font-bold tracking-[-0.5px]">
            {role === "guest" ? "Join a call" : "Ready to join?"}
          </h1>
          {/* Viewing Room: set only when a guest's kept Video call resumed here. */}
          {resumedNotice && <p className="mt-2 text-[13px] text-muted-foreground">{resumedNotice}</p>}

          {partner && (
            <div className="mt-3.5 flex items-center gap-2.5 border-b border-border pb-4">
              <Avatar initials={initialsOf(partner.name)} name={partner.name} size={38} />
              <div>
                <div className="text-[13.5px] font-semibold">{partner.name}</div>
                <div className="mt-px text-[11.5px] text-ink-3">Freelancer</div>
              </div>
            </div>
          )}

          {origin && (
            <a
              href={origin.href}
              className="mt-3.5 flex items-center justify-between rounded-xl border border-border bg-secondary px-3.5 py-3 text-[12.5px] font-semibold text-muted-foreground hover:text-foreground"
            >
              <span className="truncate">From {origin.label}</span>
              <ArrowRight className="size-3.5 shrink-0" strokeWidth={2} />
            </a>
          )}

          <div className="my-[18px] flex flex-col gap-2.5">
            <DeviceSelect label="Microphone" icon={Mic} value={vc.micId} onChange={vc.setMicId} options={vc.mics} placeholder="No Audio" container={portalNode} />
            <DeviceSelect label="Camera" icon={VideoIcon} value={vc.camId} onChange={vc.setCamId} options={vc.cameras} placeholder="No Camera" container={portalNode} />
            {vc.speakers.length > 0 && (
              <DeviceSelect label="Speaker" icon={VideoIcon} value={vc.speakerId} onChange={vc.setSpeakerId} options={vc.speakers} placeholder="Default" container={portalNode} />
            )}
          </div>

          {vc.noAudio && <p className="mb-2.5 text-[12px] font-semibold text-primary">Select an audio device to continue.</p>}
          {vc.error && <p className="mb-2.5 text-[12px] font-semibold text-primary">{vc.error}</p>}
          {vc.cameraError && <p className="mb-2.5 text-[12px] font-semibold text-primary">{vc.cameraError}</p>}

          <div className="mb-3.5 flex gap-2.5 rounded-xl border border-border bg-secondary px-3.5 py-3">
            <ShieldCheck className="mt-px size-4 shrink-0 text-ink-3" strokeWidth={2} />
            {/* The allowance is summed on the host customer alone, with no
                participant term, so it is one pool spent across every freelancer
                — say so, or the number reads as if each freelancer had their own.
                A guest is never the host, so the minutes computed for them are
                always the untouched default and mean nothing; they get the rule
                instead of a number (AT-16). */}
            <p className="text-[11.5px] leading-[1.55] text-muted-foreground">
              {recordingNotice}{" "}
              {role === "guest" ? (
                "How long this call can run is set by the customer's remaining call time."
              ) : (
                <>
                  You have <span className="font-semibold text-foreground">{limits.minutesLeftToday} minutes</span> of call time left
                  today, shared across all your freelancers — the waiting room doesn't count.
                </>
              )}{" "}
              <a href={policyUrl} target="_blank" rel="noreferrer" className="font-semibold text-primary">
                Video Call Policy
              </a>
              .
            </p>
          </div>

          {/* Host with a partner → the customer's normal waiting-room flow. */}
          {partner ? (
            <button
              type="button"
              disabled={!canStart || vc.starting}
              onClick={() => vc.startWaitingRoom(partner.id, origin)}
              className="mt-auto flex w-full items-center justify-center gap-2.5 rounded-[13px] bg-primary py-3.5 font-display text-[15px] font-semibold text-white hover:brightness-105 disabled:opacity-50"
            >
              <VideoIcon className="size-[18px]" strokeWidth={2.2} />{" "}
              {vc.starting ? "Starting…" : `Start meeting with ${partnerFirst}`}
            </button>
          ) : (
            <div className="mt-auto flex flex-col gap-2.5">
              {/* Instant "Start new meeting" is the legacy manager-only path — customers
                  reach `role: 'host'` too, so it gates on the manager flag, not role. */}
              {canStartInstant && (
                <button
                  type="button"
                  disabled={!canStart || vc.starting}
                  onClick={() => vc.createInstant("")}
                  className="flex w-full items-center justify-center gap-2.5 rounded-[13px] border border-line-2 bg-secondary py-3 font-display text-[14px] font-semibold text-foreground hover:bg-accent disabled:opacity-50"
                >
                  <VideoIcon className="size-[18px]" strokeWidth={2.2} /> Start new meeting
                </button>
              )}
              <div className="flex gap-2.5">
                <Input
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  placeholder="Room code"
                  className="flex-1 rounded-[11px] border border-border bg-secondary px-3.5 py-3 text-[13px] text-foreground outline-none placeholder:text-ink-3 h-auto shadow-none dark:bg-secondary md:text-[13px] focus-visible:border-border focus-visible:ring-0"
                />
                <button
                  type="button"
                  disabled={!canStart || !code.trim() || vc.starting}
                  onClick={() => vc.joinWithCode(code)}
                  className="rounded-[11px] bg-primary px-5 font-display text-[14px] font-semibold text-white hover:brightness-105 disabled:opacity-50"
                >
                  {vc.starting ? "Joining…" : "Join"}
                </button>
              </div>
            </div>
          )}

          {inProgress?.length > 0 && (
            <div className="mt-3.5 flex gap-2.5">
              <Select value={rejoinCode} onValueChange={setRejoinCode}>
                <SelectTrigger className="flex-1 rounded-[11px] border-border bg-secondary px-3.5 py-3 text-[13px] text-foreground">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent container={portalNode}>
                  {inProgress.map((sid) => (
                    <SelectItem key={sid} value={sid}>
                      {sid}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <button
                type="button"
                disabled={!canStart || vc.starting}
                onClick={() => vc.joinWithCode(rejoinCode)}
                className="rounded-[11px] border border-line-2 bg-secondary px-5 font-display text-[14px] font-semibold text-foreground hover:bg-accent disabled:opacity-50"
              >
                Rejoin
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

function Waiting({ vc, partner, onCopy }) {
  const first = partner?.name?.split(" ")[0] || "your freelancer"
  return (
    <div className="flex flex-1 items-center justify-center px-[22px]">
      <div className="flex w-full max-w-[460px] flex-col items-center rounded-[22px] border border-border bg-card p-11 text-center">
        <div className="relative mb-5 grid size-[118px] place-items-center">
          <span className="absolute inset-0 animate-spin rounded-full border-[3px] border-border border-t-primary" />
          <Avatar initials={initialsOf(partner?.name || "?")} name={partner?.name} size={84} />
        </div>
        <h1 className="font-display text-xl font-bold tracking-[-0.3px]">Waiting for {first} to join…</h1>
        <p className="mt-2 text-[13.5px] leading-[1.5] text-muted-foreground">
          We've sent {first} the room code in a direct message. They'll appear here as soon as they join.
        </p>
        {vc.roomCode && (
          <div className="mt-4.5 inline-flex items-center gap-2.5 rounded-[11px] border border-border bg-secondary py-2 pr-2 pl-3.5">
            <span className="text-[10.5px] font-bold tracking-[0.06em] text-ink-3 uppercase">Room</span>
            <span className="font-display text-sm font-semibold">{vc.roomCode}</span>
            <button
              type="button"
              onClick={onCopy}
              className="grid size-[30px] place-items-center rounded-lg bg-card text-muted-foreground hover:bg-primary hover:text-white"
            >
              <Copy className="size-3.5" strokeWidth={2} />
            </button>
          </div>
        )}
        <div className="mt-4.5 rounded-xl border border-border bg-secondary px-4 py-3 text-[12px] leading-[1.55] text-ink-3">
          The waiting room doesn't count toward your call-time limit. If no one joins within{" "}
          <b className="text-muted-foreground">10 minutes</b>, the session closes automatically.
        </div>
        <button
          type="button"
          onClick={vc.cancelWaiting}
          className="mt-6 inline-flex items-center gap-2 rounded-[11px] border border-line-2 px-4.5 py-2.5 text-[13px] font-semibold text-muted-foreground hover:border-primary hover:text-primary"
        >
          <PhoneOff className="size-4" strokeWidth={2} /> Cancel
        </button>
      </div>
    </div>
  )
}

function InCall({ vc, me, partner, chatOpen, setChatOpen, chatUnread }) {
  const remote = vc.participants[0] || null
  const remoteName = remote?.identity || partner?.name || "Guest"
  const localTile = (
    <Tile
      name={me.name}
      initials={initialsOf(me.name)}
      videoTrack={vc.localVideoTrack}
      cameraOff={!vc.camOn}
      muted={!vc.micOn}
      mirror
      self
    />
  )
  const remoteTile = (
    <Tile
      name={remoteName}
      initials={initialsOf(remoteName)}
      videoTrack={remote?.videoTrack}
      cameraOff={remote ? remote.cameraOff : true}
      muted={remote ? remote.muted : false}
    />
  )

  return (
    <>
      <div className="flex min-h-0 flex-1 gap-4 px-[22px] pt-1 pb-5">
        <div className="flex min-w-0 flex-1 flex-col">
          {vc.screenOn ? (
            // The shared screen is the thing both people are looking at, but one
            // desktop arrangement was applied at every width: a fixed 188px tile
            // column beside it left roughly 142px for the screen on a phone. Below
            // 920 the stage takes the full width and the tiles become a short strip
            // under it; from 920 up the original composition is untouched. Tailwind
            // v4's `max-[920px]` edge is exclusive — it is `width < 920` (AT-17).
            <div className="flex min-h-0 flex-1 gap-4 max-[920px]:flex-col">
              <div className="relative min-h-0 min-w-0 flex-1 overflow-hidden rounded-[18px] border border-border bg-background">
                {vc.remoteScreenTrack ? (
                  <VideoSurface track={vc.remoteScreenTrack} fit="contain" className="bg-background" />
                ) : (
                  <div className="flex h-full items-center justify-center text-[13px] text-ink-3">You're sharing your screen</div>
                )}
              </div>
              <div className="flex w-[188px] shrink-0 flex-col gap-3 max-[920px]:grid max-[920px]:h-[124px] max-[920px]:w-full max-[920px]:grid-cols-2">
                {remoteTile}
                {localTile}
              </div>
            </div>
          ) : (
            // Two stacked full-width rows make a desktop tile ~4:1, and `cover`
            // then shows a band across the middle of a 4:3 camera frame. Side by
            // side from `md` up puts the tile back near the capture aspect, so the
            // crop is a little off each side instead of most of the face (AT-10).
            <div className="grid min-h-0 flex-1 grid-rows-2 gap-4 md:grid-cols-2 md:grid-rows-1">
              {remoteTile}
              {localTile}
            </div>
          )}
        </div>

        {chatOpen && <SideChat conversationSid={vc.conversationSid} onClose={() => setChatOpen(false)} />}
      </div>

      {/* Attach remote audio out of the tile so it survives layout changes. */}
      {remote?.audioTrack && <AudioSurface track={remote.audioTrack} sinkId={vc.speakerId} />}

      <ControlBar
        micOn={vc.micOn}
        camOn={vc.camOn}
        canScreen={vc.canScreenShare}
        screenOn={vc.screenOn}
        chatOpen={chatOpen}
        chatUnread={chatUnread}
        onMic={vc.toggleMic}
        onCam={vc.toggleCam}
        onScreen={vc.toggleScreenShare}
        onChat={() => setChatOpen((v) => !v)}
        onLeave={vc.leave}
      />
    </>
  )
}

function Ended({ vc, partner, recordingNotice }) {
  const secs = vc.endedSummary?.seconds ?? 0
  const mm = Math.floor(secs / 60)
  const ss = secs % 60
  return (
    <div className="flex flex-1 items-center justify-center px-[22px]">
      <div className="flex w-full max-w-[480px] flex-col items-center rounded-[22px] border border-border bg-card p-10 text-center">
        <div className="mb-4.5 grid size-[62px] place-items-center rounded-[18px] border border-border bg-secondary">
          <Check className="size-7 text-primary" strokeWidth={1.9} />
        </div>
        <h1 className="font-display text-[22px] font-bold tracking-[-0.4px]">Call ended</h1>
        <div className="my-3.5 font-display text-[40px] font-bold tracking-[-1.5px] tabular-nums">
          {mm}:{ss < 10 ? "0" : ""}
          {ss}
        </div>
        {partner && <div className="text-[13.5px] text-muted-foreground">with {partner.name}</div>}
        <div className="my-5.5 flex w-full items-center gap-2.5 rounded-xl border border-border bg-secondary px-3.5 py-3 text-left">
          <Circle className="size-4 shrink-0 fill-primary text-primary" strokeWidth={2} />
          <p className="text-[11.5px] leading-[1.5] text-muted-foreground">
            {recordingNotice}
          </p>
        </div>
        <div className="flex w-full gap-2.5">
          <a
            href="/messages"
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-line-2 bg-secondary py-3 font-display text-[13.5px] font-semibold text-foreground hover:bg-accent"
          >
            <MessageSquare className="size-4" strokeWidth={2.1} /> Back to messages
          </a>
          <button
            type="button"
            onClick={vc.restart}
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-3 font-display text-[13.5px] font-semibold text-white hover:brightness-105"
          >
            <VideoIcon className="size-4" strokeWidth={2.1} /> Start another call
          </button>
        </div>
      </div>
    </div>
  )
}

function LimitReached({ limits, policyUrl }) {
  // Two caps guard one shared pool, and the panel used to describe only the
  // daily one — so a customer who had spent the month was told to come back
  // tonight and found the same closed door. `limitKind` says which tripped, and
  // arrives with the props that put the page in this phase; anything else falls
  // back to the daily reading, which is what this panel always said (AT-16).
  const monthly = limits.limitKind === "monthly"
  const dailyUsed = Math.max(0, limits.dailyLimit - limits.minutesLeftToday)
  const dailyPct = limits.dailyLimit > 0 ? Math.min(100, Math.round((dailyUsed / limits.dailyLimit) * 100)) : 100
  return (
    <div className="flex flex-1 items-center justify-center px-[22px]">
      <div className="flex w-full max-w-[470px] flex-col items-center rounded-[22px] border border-border bg-card p-10 text-center">
        <div className="mb-4.5 grid size-[62px] place-items-center rounded-[18px] bg-needs-you/15">
          <AlertTriangle className="size-7 text-needs-you" strokeWidth={1.9} />
        </div>
        <h1 className="font-display text-[21px] font-bold tracking-[-0.3px]">
          {monthly ? "You've used this month's call time" : "You've used today's call time"}
        </h1>
        <p className="mt-2.5 max-w-[40ch] text-[13.5px] leading-[1.6] text-muted-foreground">
          Call time is shared across all your freelancers, not set aside for each one.{" "}
          {monthly
            ? `Your ${limits.monthlyLimitHours} hours for the month are used up — the allowance resets at the start of next month.`
            : "Your daily allowance resets tonight."}
        </p>
        <div className="my-6 flex w-full flex-col gap-3.5">
          <div>
            <div className="mb-[7px] flex justify-between text-[12px]">
              <span className="font-semibold text-muted-foreground">{monthly ? "This month" : "Today"}</span>
              <span className="tabular-nums text-ink-3">
                {monthly
                  ? `${limits.monthlyLimitHours} / ${limits.monthlyLimitHours} hrs`
                  : `${dailyUsed} / ${limits.dailyLimit} min`}
              </span>
            </div>
            <div className="h-2 overflow-hidden rounded-md bg-background">
              <div className="h-full rounded-md bg-needs-you" style={{ width: `${monthly ? 100 : dailyPct}%` }} />
            </div>
          </div>
        </div>
        <div className="flex w-full gap-2.5">
          <a
            href="/messages"
            className="flex flex-1 items-center justify-center gap-2 rounded-xl border border-line-2 bg-secondary py-3 font-display text-[13.5px] font-semibold text-foreground hover:bg-accent"
          >
            <MessageSquare className="size-4" strokeWidth={2.1} /> Message instead
          </a>
          <a
            href={policyUrl}
            target="_blank"
            rel="noreferrer"
            className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-primary py-3 font-display text-[13.5px] font-semibold text-white hover:brightness-105"
          >
            <ArrowRight className="size-4" strokeWidth={2.1} /> Learn about limits
          </a>
        </div>
      </div>
    </div>
  )
}

// ===========================================================================
export default function VideoCall(props) {
  const {
    role,
    partner,
    me,
    limits,
    inProgress,
    policyUrl,
    recordingNotice,
    canStartInstant,
    origin,
    initialRoomCode,
    resumedNotice,
  } = props
  const liveRoom = useVideoRoom({ initialLimitReached: limits.limitReached })
  // Passes the live room straight through everywhere except a dev build visited
  // with `?call_preview=`, which mounts this same page in-call against synthetic
  // tracks so its geometry can be measured without a live Twilio room. The whole
  // seam is dropped from a production build (AT-17).
  const vc = useCallPreview(liveRoom)
  const [chatOpen, setChatOpen] = useState(false)
  const [lastSeenId, setLastSeenId] = useState(0)
  const [copied, setCopied] = useState(false)
  // Capture the `.dark` full-screen wrapper as the portal container for Select
  // content so portaled menus render dark, not light-on-body. Callback-ref into
  // state, not useRef().current — the latter is null at first paint and Radix
  // reads `container` lazily when the select opens.
  const [portalNode, setPortalNode] = useState(null)

  useEffect(() => {
    vc.initDevices()
    // Devices are enumerated once on mount; the hook owns re-selection.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // ---- in-call chat: one poll for the whole call, plus the unread marker -----
  // The poll used to live inside SideChat, which unmounts when the panel closes,
  // so while the panel was shut nothing fetched messages and there was no unread
  // state to render at all. It runs here for as long as the call is up; the panel
  // is now just a view of what the page has already fetched (AT-11). Folding the
  // sid into the URL is what lets the send path's bare `only: ['sideChat']`
  // reload rebuild this thread too.
  const chatMessages = props.sideChat?.messages
  useEffect(() => {
    if (vc.phase !== "incall" || !vc.conversationSid) return undefined
    const reload = () =>
      router.reload({
        only: ["sideChat"],
        data: { conversation_sid: vc.conversationSid },
        preserveState: true,
        preserveScroll: true,
      })
    reload()
    const id = setInterval(reload, POLL_MS)
    return () => clearInterval(id)
  }, [vc.phase, vc.conversationSid])

  // With the panel open every message is on screen, so the marker follows the
  // thread. With it shut the marker is frozen, and anything past it that isn't
  // ours is unread. The thread is created with the room, so it starts empty and
  // a marker of 0 counts nothing that predates the call.
  const newestChatId = useMemo(() => (chatMessages ?? []).reduce((n, m) => (m.id > n ? m.id : n), 0), [chatMessages])
  useEffect(() => {
    if (chatOpen) setLastSeenId((seen) => (newestChatId > seen ? newestChatId : seen))
  }, [chatOpen, newestChatId])
  const chatUnread = chatOpen ? 0 : (chatMessages ?? []).filter((m) => !m.mine && m.id > lastSeenId).length

  const copyRoom = () => {
    if (!vc.roomCode) return
    navigator.clipboard?.writeText(vc.roomCode).catch(() => {})
    setCopied(true)
    setTimeout(() => setCopied(false), 1600)
  }

  const warn = vc.timeLeft != null && vc.timeLeft <= 120
  const showTop = vc.phase === "incall" || vc.phase === "waiting" || vc.phase === "prejoin"

  return (
    <div ref={setPortalNode} className="dark fixed inset-0 flex flex-col bg-background font-sans text-foreground">
      {showTop && (
        <TopBar
          room={vc.phase === "incall" || vc.phase === "waiting" ? vc.roomCode : null}
          timeLeftLabel={vc.phase === "incall" ? vc.timeLeftLabel : null}
          warn={warn}
          contextLabel={origin?.label ? `${origin.label} · ${partner?.name ? `Call with ${partner.name}` : "Video call"}` : partner?.name ? `Call with ${partner.name}` : null}
          onCopy={copyRoom}
        />
      )}

      {vc.phase === "prejoin" && (
        <PreJoin
          vc={vc}
          role={role}
          partner={partner}
          me={me}
          limits={limits}
          policyUrl={policyUrl}
          recordingNotice={recordingNotice}
          inProgress={inProgress}
          canStartInstant={canStartInstant}
          portalNode={portalNode}
          origin={origin}
          initialRoomCode={initialRoomCode}
          resumedNotice={resumedNotice}
        />
      )}
      {vc.phase === "waiting" && <Waiting vc={vc} partner={partner} onCopy={copyRoom} />}
      {vc.phase === "incall" && (
        <InCall vc={vc} me={me} partner={partner} chatOpen={chatOpen} setChatOpen={setChatOpen} chatUnread={chatUnread} />
      )}
      {vc.phase === "ended" && <Ended vc={vc} partner={partner} recordingNotice={recordingNotice} />}
      {vc.phase === "limit" && <LimitReached limits={limits} policyUrl={policyUrl} />}

      {copied && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 rounded-lg border border-border bg-card px-4 py-2 text-[12.5px] font-semibold text-foreground shadow-lg">
          Room code copied — share it with your freelancer
        </div>
      )}
    </div>
  )
}

// No `Page.layout` — that is how the surface escapes the AppShell and renders in
// its own dark full-screen layout (Rails seam: the clean `inertia` layout body).
